import type { BtlApplication } from 'api/btlApplicationForm/types';
import { btlApplicationFormApi } from 'api/btlApplicationForm';
import { AddressHistory, IndividualCreateDto } from 'api/btlApplicationForm/types';
import { App } from 'antd';
import type { OoCaseSidebarContext } from 'components/CaseSidebar/buildOoCaseSidebar';
import { CaseSidebarItemId } from 'components/CaseSidebar/types';
import { FormValidationContext } from 'hooks';
import { individualFormSchema } from 'pages/BtlApplicationFormPage/Applicants/Individual/formSchema';
import { mapIndividualToFormValues } from 'pages/BtlApplicationFormPage/Applicants/Individual/mapIndividualToFormValues';
import { invalidateBtlApplicationData } from 'pages/BtlApplicationFormPage/btl-utils';
import { transformAddress } from 'pages/BtlApplicationFormPage/common/Address';
import { makeFormReadonly } from 'pages/BtlApplicationFormPage/makeFormReadonly';
import {
  normalizeIndividualIncomesForForm,
  mapFormIncomesPayloadToDto,
} from 'pages/BtlApplicationFormPage/TopSlicing/Household/mapIndividualIncomesToFormValues';
import { useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { isValidGbPhoneNumber } from 'utils/gbPhoneNumber';

interface UseOoCaseApplicantIndividualFormParams {
  caseData?: BtlApplication;
  individualId?: string | null;
  isCaseLoading: boolean;
  sidebarContext: OoCaseSidebarContext;
}

export function useOoCaseApplicantIndividualForm({
  caseData,
  individualId,
  isCaseLoading,
  sidebarContext,
}: UseOoCaseApplicantIndividualFormParams) {
  const { formatMessage } = useIntl();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const formValidationContext = useContext(FormValidationContext);

  const individualData = caseData?.borrower.individuals.find(individual => individual.id === individualId);
  const isFirstIndividual = caseData?.borrower.individualOrder[0] === individualId;
  const isFormEditable = caseData?.status === 'DIP_ACCEPTED' && sidebarContext.canEditDipAcceptedSections;
  const isDipAccepted = isFormEditable;

  if (isFormEditable && !formValidationContext) {
    throw new Error('OO applicant individual form must be used within a FormValidationProvider when editable');
  }

  const setIsSubmitting = formValidationContext?.setIsSubmitting ?? (() => undefined);

  if ((isCaseLoading && !caseData) || !caseData || !individualId || !individualData) {
    return { isLoading: true as const };
  }

  const schemaProps = {
    formatMessage,
    variant: 'oo' as const,
    isFormEditable,
    isDipAccepted,
    isDipAcceptedOrLater: isDipAccepted,
    showGrossIncome: false,
    hasCompanyApplicant: false,
    isFirstIndividual: Boolean(isFirstIndividual),
  };

  const formSchema = individualFormSchema(schemaProps);
  const adjustedFormSchema = isFormEditable ? formSchema : makeFormReadonly(formSchema);

  return {
    isLoading: false as const,
    isFormEditable,
    formKey: `${caseData.id}-${individualId}`,
    footerProps: isFormEditable
      ? {
          submitLabel: formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.nextLabel' }),
        }
      : {},
    formProps: {
      columns: adjustedFormSchema,
      isFormEditable,
      name: 'oo_applicant_individual',
      params: { caseId: caseData.id, individualId },
      scrollToFirstError: true,
      request: async () => {
        const incomeValues = await btlApplicationFormApi.getIndividualIncomes(
          caseData.id,
          caseData.borrower.id,
          individualId,
        );
        const incomes = incomeValues?.incomes ?? individualData.incomes ?? [];

        return {
          ...mapIndividualToFormValues(individualData),
          ...incomeValues,
          incomes: normalizeIndividualIncomesForForm(incomes, individualData.incomes ?? []),
        };
      },
      onFinishFailed: () => {
        message.error(formatMessage({ id: 'common.error.requiredField' }));
      },
      onFinish: isFormEditable
        ? async (values: any) => {
            const {
              addressList,
              email,
              phoneNumber,
              previousNames,
              residencyStatus,
              relationshipToPrimaryApplicant,
              relationshipToSpv,
              incomes,
              otherIncome,
              ...rest
            } = values;

            const trimmedPhone = String(phoneNumber ?? '').trim();
            if (trimmedPhone && !isValidGbPhoneNumber(trimmedPhone)) {
              message.error(formatMessage({ id: 'common.error.validPhoneNumber' }));
              return;
            }

            let addressHistory = transformAddress(addressList);
            addressHistory = addressHistory.map((entry: AddressHistory, index: number) => {
              if (index === 0) {
                return { ...entry, isCurrentAddress: true, moveOutDate: null };
              }

              return { ...entry, isCurrentAddress: false };
            });

            const contactDetails = [
              email && { contact: email, contactType: 'EMAIL' },
              phoneNumber && { contact: phoneNumber, contactType: 'PHONE' },
            ].filter(Boolean);

            const transformedValues: IndividualCreateDto = {
              ...rest,
              addressHistory,
              contactDetails,
              ...(previousNames?.length
                ? {
                    previousTitle: previousNames[0]?.title,
                    previousFirstName: previousNames[0]?.firstName,
                    previousMiddleName: previousNames[0]?.middleName,
                    previousLastName: previousNames[0]?.lastName,
                  }
                : {}),
              ...(residencyStatus?.status
                ? {
                    residencyStatus: {
                      ...residencyStatus,
                    },
                  }
                : { residencyStatus: { status: 'UNKNOWN', visaEndDate: null, dualNationality: null } }),
              isPrimary: Boolean(individualData?.isPrimary),
              relationshipToPrimaryApplicant: individualData?.isPrimary ? null : relationshipToPrimaryApplicant,
              relationshipToSpv,
              isVulnerable: null,
              vulnerabilityCategory: null,
              vulnerabilityDetails: null,
              vulnerabilityDateIdentified: null,
            };

            const valuesWithIsPrimary = mapFormIncomesPayloadToDto(
              { incomes, otherIncome },
              individualData.incomes ?? [],
            );

            try {
              setIsSubmitting(true);

              if (!caseData.borrower.id) {
                throw new Error('A Borrower id is required');
              }

              await btlApplicationFormApi.editIndividual(
                caseData.id,
                caseData.borrower.id,
                individualId,
                transformedValues,
              );
              await btlApplicationFormApi.createIndividualIncome(
                caseData.id,
                caseData.borrower.id,
                individualId,
                valuesWithIsPrimary,
              );
              await invalidateBtlApplicationData(queryClient, caseData.id);
              await queryClient.invalidateQueries({
                queryKey: ['btl-application-get-individual-incomes', caseData.id, caseData.borrower.id, individualId],
              });

              navigate({ search: `?section=${CaseSidebarItemId.APPLICANTS}` });
            } catch (error: unknown) {
              console.error(error);
              message.error(error instanceof Error ? error.message : 'Failed to save applicant details');
            } finally {
              setIsSubmitting(false);
            }
          }
        : undefined,
    },
  };
}
